import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Crown, Ticket, Sparkles, Mail, Gem, Headphones, Loader2, Check, Copy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import AuthModal from "@/components/AuthModal";
import Rewards from "@/pages/Rewards";
import { toast } from "sonner";

const BENEFITS = [
  { icon: Ticket, title: "Exclusive VIP coupons", text: "Members-only discount codes you can use across the marketplace." },
  { icon: Sparkles, title: "Early access", text: "Shop new listings and sales before anyone else." },
  { icon: Mail, title: "Monthly updates by email", text: "A monthly VIP newsletter with trends, drops & fresh coupons in your inbox." },
  { icon: Gem, title: "VIP badge", text: "A gold VIP badge on your profile and reviews." },
  { icon: Headphones, title: "Priority support", text: "Skip the line with priority customer care." },
];

export default function VIP() {
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  usePageMeta(
    "VIP Membership — Exclusive Beauty Perks | Beauty Trade Club",
    "Join Beauty Trade Club VIP for $4.99/month — exclusive coupons, early access to new listings, a gold VIP badge, monthly beauty drops and priority support.",
    "/vip"
  );
  const [busy, setBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [refCreator, setRefCreator] = useState(null);
  const sessionId = params.get("session_id");
  const ref = params.get("ref");

  useEffect(() => {
    if (!ref) return;
    api.get(`/influencer/by-code/${ref}`).then(({ data }) => setRefCreator(data)).catch(() => setRefCreator(null));
  }, [ref]);

  useEffect(() => {
    if (!sessionId) return;
    setChecking(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/vip/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "complete") {
          await refreshUser();
          toast.success("Welcome to VIP! 👑");
          setChecking(false);
          return;
        }
      } catch { /* keep polling */ }
      if (attempts++ < 6) setTimeout(poll, 2000);
      else setChecking(false);
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  const subscribe = async () => {
    if (!user) { setAuthOpen(true); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/vip/subscribe", { origin_url: window.location.origin, ref: ref || undefined });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const copyCoupon = () => {
    navigator.clipboard.writeText(user.vip_coupon);
    toast.success("Coupon copied!");
  };

  const isVip = user?.is_vip;

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-[#E8956B]/15 text-[#CE744A] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.25em] mb-5">
          <Crown size={15} /> Beauty Trade Club VIP
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black">Become a VIP Member</h1>
        <p className="text-neutral-600 mt-4 max-w-xl mx-auto text-lg">
          Unlock exclusive perks, early access and monthly coupons delivered to your inbox — for just{" "}
          <span className="font-bold text-neutral-900">$4.99/month</span>.
        </p>
      </div>

      {checking && (
        <div className="text-center mb-8 flex items-center justify-center gap-2 text-neutral-500" data-testid="vip-checking">
          <Loader2 className="animate-spin" size={18} /> Confirming your membership...
        </div>
      )}

      {isVip ? (
        <div data-testid="vip-member-card" className="bg-gradient-to-br from-neutral-900 to-neutral-800 text-white rounded-3xl p-8 mb-12 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#E8956B] font-bold uppercase tracking-[0.2em] text-sm">
              <Crown size={18} /> VIP Member
            </div>
            <Check className="text-[#E8956B]" />
          </div>
          <p className="mt-4 text-neutral-300">Hi {user.name}, your VIP perks are active. Member since {new Date(user.vip_since).toLocaleDateString()}.</p>
          <div className="mt-6 bg-white/10 rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-400 mb-1">Your exclusive coupon</div>
            <div className="flex items-center justify-between">
              <span data-testid="vip-coupon" className="font-display text-2xl font-black text-[#E8956B] tracking-wider">{user.vip_coupon}</span>
              <button data-testid="copy-coupon-btn" onClick={copyCoupon} className="flex items-center gap-1 text-sm bg-[#E8956B] text-white font-bold rounded-full px-4 py-2 hover:bg-[#CE744A] transition-all">
                <Copy size={14} /> Copy
              </button>
            </div>
          </div>
          <p className="mt-4 text-sm text-neutral-400 flex items-center gap-2">
            <Mail size={14} /> Watch your inbox ({user.email}) — we send fresh coupons & beauty drops every month.
          </p>
        </div>
      ) : (
        <div className="text-center mb-12">
          {refCreator && (
            <div data-testid="vip-ref-banner" className="mb-5 inline-flex items-center gap-2 bg-[#E8956B]/12 text-[#CE744A] px-5 py-2.5 rounded-full text-sm font-semibold">
              <Crown size={15} /> You're joining through {refCreator.name} — they'll earn a little when you do. 💛
            </div>
          )}
          <button data-testid="vip-subscribe-btn" onClick={subscribe} disabled={busy}
            className="bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-10 py-4 text-lg btn-glow transition-all disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <><Loader2 size={20} className="animate-spin" /> Redirecting...</> : <><Crown size={20} /> Join VIP · $4.99/mo</>}
          </button>
          <p className="text-xs text-neutral-400 mt-3">Cancel anytime. Secure payment via Stripe.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        {BENEFITS.map((b) => (
          <div key={b.title} className="flex gap-4 bg-white border border-neutral-200 rounded-2xl p-5">
            <div className="w-11 h-11 shrink-0 rounded-full bg-[#E8956B]/15 flex items-center justify-center">
              <b.icon className="text-[#E8956B]" size={20} />
            </div>
            <div>
              <h3 className="font-bold">{b.title}</h3>
              <p className="text-sm text-neutral-600 mt-1">{b.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* REWARDS (merged in) */}
      <div className="mt-16 pt-12 border-t border-neutral-200" data-testid="vip-rewards-section">
        <Rewards embedded />
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} onSuccess={subscribe} />
    </div>
  );
}
